import { HeadingDefaultSpacing, HeadingSize21, HeadingSize22 } from './types';

/**
 * Default spacing values for each heading size.
 *
 * This is intentionally decoupled from the Chakra theme's `sizes` config.
 * When spacing is defined inside `sizes`, Chakra applies it via CSS media queries,
 * which have higher specificity than user-provided props. This means overrides
 * like `my={0}` would be ignored.
 *
 * By keeping spacing separate and applying it via props (see `getSpacingProps` in
 * `./utils`), we allow users to override spacing while still providing sensible
 * defaults that scale with the heading size (or disable spacing via noDefaultSpacing).
 *
 * Both versions live here rather than beside their implementations so the deltas are readable
 * in one place. There are none: every 2.2 value is its 2.1 counterpart's under the size mapping
 * (`title-xs`→`title-200`, `sm`→`400`, `md`→`600`, `lg`→`700`, `xl`→`800`, `jumbo-xl`→`900`), so
 * migrating a call site never changes what sits above or below the heading. The type metrics do
 * move on some steps — block 14 of the `?content=typography` draft page measures each pair.
 *
 * @see ./utils/get-spacing-props.ts for how these values are applied
 */
export const defaultSpacing21: HeadingDefaultSpacing<HeadingSize21> = {
  'title-xs': { my: '2' },
  'title-sm': { my: '4' },
  'title-md': { my: '4' },
  'title-lg': { my: '6' },
  'title-xl': { my: '8' },
  'title-jumbo-xl': {},
};

/** DLS 2.2 — the ramp the designer's prototype applies to the numeric scale. */
export const defaultSpacing22: HeadingDefaultSpacing<HeadingSize22> = {
  'title-50': { my: '2' },
  'title-100': { my: '2' },
  'title-200': { my: '2' },
  'title-300': { my: '4' },
  'title-400': { my: '4' },
  'title-500': { my: '4' },
  'title-600': { my: '4' },
  'title-700': { my: '6' },
  'title-800': { my: '8' },
  'title-900': {},
};
